"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { MarketingLogo } from "@/components/MarketingLogo";
import { Link } from "@/i18n/navigation";
import {
  denyAdsConsent,
  getStoredCookieConsent,
  hasConsentedTagsOnPage,
  isRegionalConsentEnabled,
  openCookieSettings,
  reloadPage,
} from "@/lib/analytics";
import {
  footerCompanyLinks,
  footerLegalLinks,
  footerProductLinks,
} from "@/content/nav";

import { LanguageSwitcher } from "../LanguageSwitcher";
import { ThemeToggle } from "../ThemeToggle";
import { SocialLinks } from "./subcomponents/SocialLinks";

export const Footer = () => {
  const t = useTranslations();
  const year = new Date().getFullYear();
  const [adsOptOutSaved, setAdsOptOutSaved] = useState(false);

  // The opt-out survives the reload below, so keep confirming it.
  useEffect(() => {
    setAdsOptOutSaved(getStoredCookieConsent() === "analytics");
  }, []);

  const linkClassName =
    "text-sm text-[color-mix(in_oklch,var(--foreground)_75%,transparent)] transition-colors hover:text-foreground";

  const linkGroup = (
    titleKey: string,
    links: { labelKey: string; href: string; external?: boolean }[],
  ) => (
    <div className="flex flex-col gap-4">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
        {t(titleKey)}
      </h3>
      <ul className="flex flex-col gap-2">
        {links.map((link) => {
          const isExternal = link.external || /^https?:\/\//.test(link.href);
          return (
            <li key={link.href}>
              {isExternal ? (
                <a
                  href={link.href}
                  className={linkClassName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(link.labelKey)}
                </a>
              ) : (
                <Link href={link.href} className={linkClassName}>
                  {t(link.labelKey)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <footer className="border-t border-border bg-background dark:bg-[var(--marketing-dark-canvas)]">
      <div className="marketing-grid flex flex-col gap-12 pb-10 pt-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-4">
            <MarketingLogo imageClassName="h-8" />
            <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
              {t("footer.tagline")}
            </p>
            <p className="text-sm font-medium text-foreground">
              {t("footer.builtWithCrew")}
            </p>
            <SocialLinks />
          </div>
          {linkGroup("footer.product", footerProductLinks)}
          {linkGroup("footer.company", footerCompanyLinks)}
          {linkGroup("footer.legal", footerLegalLinks)}
        </div>
        <div className="flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <p className="text-xs text-muted-foreground">
              {t("footer.copyright", { year })}
            </p>
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-left text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              {t("footer.cookieSettings")}
            </button>
            {isRegionalConsentEnabled() ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    const tagsToRemove = hasConsentedTagsOnPage();
                    denyAdsConsent();
                    setAdsOptOutSaved(true);
                    // Ad tags already loaded keep running until the page reloads without them.
                    if (tagsToRemove) {
                      reloadPage();
                    }
                  }}
                  className="text-left text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  {t("footer.doNotSell")}
                </button>
                <span
                  role="status"
                  className="text-xs text-muted-foreground empty:hidden"
                >
                  {adsOptOutSaved ? t("footer.doNotSellSaved") : null}
                </span>
              </>
            ) : null}
          </div>
          <div className="grid w-full max-w-sm grid-cols-2 gap-4 sm:gap-6">
            <LanguageSwitcher variant="footer" />
            <div className="flex w-full flex-col items-stretch gap-2 text-sm text-muted-foreground">
              <span className="shrink-0 text-[11px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
                {t("footer.appearance")}
              </span>
              <ThemeToggle className="inline-flex h-10 w-10 shrink-0 rounded-lg border border-zinc-200/50 bg-muted/30 text-foreground shadow-none hover:bg-muted/50 dark:border-white/[0.05] dark:bg-white/[0.04] dark:hover:bg-white/[0.06] dark:hover:text-foreground" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
